// Contractor compliance portal — RON-5465.
//
// A PUBLIC PAGE. No Ronspot login, no sidebar, no app chrome: the caller is a contractor outside
// the customer's company who arrived from an email. It renders its own header and footer and fills
// the viewport, the same way the Reception tab does.
//
// ⚠ IF A GLOBAL AUTH GUARD IS EVER ADDED AROUND THE ROUTES, THIS ONE MUST STAY OUTSIDE IT. There
// is nobody to log in. The protection here is the emailed code, not a session the app controls.
//
// The session token lives in `PortalAPI.jsx`, in memory, for the life of the tab — see the note
// there for why it is not in storage.

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import PortalVerify from "./components/PortalVerify.jsx";
import PortalExpired from "./components/PortalExpired.jsx";
import PortalPackHeader from "./components/PortalPackHeader.jsx";
import PortalClearanceBanner from "./components/PortalClearanceBanner.jsx";
import PortalToast from "./components/PortalToast.jsx";
import PortalSection from "./components/PortalSection.jsx";
import PortalReadSign from "./components/PortalReadSign.jsx";
import PortalUploads from "./components/PortalUploads.jsx";
import {
  clearPortalToken,
  confirmPortalPersonnel,
  fetchPortalDocument,
  fetchPortalPack,
  requestPortalCode,
  setLinkToken,
  signPortalDocument,
  submitPortalPack,
  uploadPortalDocument,
  openPortal,
  verifyPortalCode,
} from "./PortalAPI.jsx";
import {
  STEP,
  firstOpenStep,
  formatLongDate,
  formatShortDate,
  isStepDone,
  nextOpenStep,
} from "./portalConstants.js";
import ronspotBlueLogo from "../../assets/images/ronspot-blue-logo.svg";

const CompliancePortalUI = () => {
  const { t } = useTranslation("common");

  // The LINK TOKEN from `/portal/:token` — renamed from `:guid` on 22-October-2026, when the
  // contract replaced the guid with a 43-character token ("Phase A: token links").
  //
  // It says WHICH pack and nothing else; the emailed code is what proves who. Unlike the guid it
  // replaced it DOES expire — 30 days by default — and a newer link closes it along with any
  // session opened through it.
  //
  // Handed to the API layer rather than threaded through each call, because only the sign-in
  // endpoints take it. Set before anything can be requested, so a reload mid-session still knows
  // which pack to ask about.
  //
  const { token = "" } = useParams();

  useEffect(() => {
    setLinkToken(token);
  }, [token]);

  // ── `open` — the first call, before anything is drawn ─────────────────────────
  //
  // Three outcomes: a live link shows the code screen, a closed one shows the expired screen and
  // nothing else, and an unknown token shows the same screen with no date and no contact.
  //
  // It has to come BEFORE the code screen, not alongside it. `request-code` answers identically
  // for a live link and a dead one — deliberately — so without `open` a contractor holding a
  // stale link would type a code that can never arrive, five times, and hit the rate limit.
  const [link, setLink] = useState(null);
  // The customer's name as `open` gives it — BEFORE there is a pack or a session.
  //
  // We used to leave every name blank until sign-in, on the grounds that naming the customer
  // would confirm to anyone holding a forwarded link that the guid was real. That held while
  // `request-code` was the only pre-sign-in call, because it answers identically for a live
  // pack, an unknown guid and a finished one.
  //
  // Phase A ended it: `open` 404s an unknown token and returns `company` for a live one, so the
  // server itself draws the distinction before a code is ever requested. Withholding the name on
  // screen now hides it from the reader and from nobody else.
  const [linkCompany, setLinkCompany] = useState(null);

  // The rest of what `open` returns about a LIVE link — logo, masked address, who sent it, and
  // the pack's reference (answers-to-frontend-21, 24-October-2026).
  //
  // Kept as one object rather than four `useState`s: they arrive together in one response and
  // are never set apart from each other, so four setters would be four chances to update three.
  const [linkDetails, setLinkDetails] = useState(null);
  const [closed, setClosed] = useState(null);
  const [isOpening, setIsOpening] = useState(true);

  useEffect(() => {
    if (!token) return undefined;

    let cancelled = false;

    (async () => {
      setIsOpening(true);

      const { ok, response, open } = await openPortal();

      if (cancelled) return;

      setIsOpening(false);

      if (!ok) {
        // A 404 — an unknown token, or a live link to a finished pack. The same screen as a
        // closed link, with no date and no contact, carrying the server's own sentence.
        setClosed({
          closedAt: null,
          timezone: null,
          contact: null,
          message: response?.Data?.ErrorMessage || t("react_portal_error_generic"),
        });

        return;
      }

      if (open.state === "expired") {
        // The logo is the ONLY thing added to this reply. Everything else the expired screen
        // shows is unchanged, and it still names no company and no pack — RON-4831 §1.1.
        setLinkDetails({ logoUrl: open.logoUrl });

        setClosed({
          closedAt: open.closedAt,
          timezone: open.timezone,
          contact: open.contact,
          message: response?.Data?.Message || null,
        });

        return;
      }

      setLink(open.link);
      setLinkCompany(open.company);
      setLinkDetails({
        logoUrl: open.logoUrl,
        maskedEmail: open.maskedEmail,
        contact: open.contact,
        reference: open.reference,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [token, t]);

  // Whether we have asked for a code, and how many times. Ours to track: the server tells us
  // nothing beyond "accepted", and the count is only used to show the contractor where the
  // 5-per-hour ceiling is before they hit it.
  const [codeRequested, setCodeRequested] = useState(false);
  const [codeRequestCount, setCodeRequestCount] = useState(0);
  const [isVerified, setIsVerified] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState(null);
  // The success line. Separate from `error` on purpose: one fades and one does not, because a
  // confirmation is finished with once read and a problem is not.
  const [notice, setNotice] = useState(null);

  // STABLE, via useCallback, and that is not a micro-optimisation. `PortalToast` runs its
  // four-second timer in an effect keyed on this function; an inline arrow would be a new
  // reference every render, so every re-render would clear and restart the timer and the toast
  // would sit there until the screen went quiet.
  const clearNotice = useCallback(() => setNotice(null), []);

  // ── The pack ────────────────────────────────────────────────────────────────
  //
  // §4: EVERY WRITE RETURNS THE WHOLE PACK, not the row that changed. So every handler below
  // replaces the pack wholesale and nothing patches a document in place — which is exactly the
  // class of bug the contract designed out, where step 4's count drifts from the list above it.
  const [pack, setPack] = useState(null);
  const [bodies, setBodies] = useState({});
  const [openStep, setOpenStep] = useState(STEP.READ);
  const [isWriting, setIsWriting] = useState(false);

  // Revoke on unmount. A tab closed mid-pack should not leave a usable session behind it.
  useEffect(() => () => clearPortalToken(), []);

  useEffect(() => {
    if (!isVerified) return undefined;

    let cancelled = false;

    (async () => {
      const { ok, pack: loaded } = await fetchPortalPack();

      if (cancelled || !ok) return;

      setPack(loaded);
      // Open the first unfinished step rather than making them scroll past what they have done.
      setOpenStep(firstOpenStep(loaded));
    })();

    return () => {
      cancelled = true;
    };
  }, [isVerified]);

  const onRequestCode = useCallback(async () => {
    setIsBusy(true);
    setError(null);

    const { ok, response } = await requestPortalCode();

    setIsBusy(false);

    if (!ok) {
      // Includes the 400 rate limit, "Code request limit reached (5 per hour)." — shown in the
      // server's own words because it names the ceiling and we would only paraphrase it.
      setError(response?.Data?.ErrorMessage || t("react_portal_error_generic"));

      return;
    }

    // A success tells us only that the request was accepted (§1: "`sent: true` means 'we are not
    // telling you'"), so this flag records what WE did, not what the mail server managed.
    setCodeRequested(true);
    setCodeRequestCount((count) => count + 1);
  }, [t]);

  const onVerify = useCallback(
    async (code) => {
      setIsBusy(true);
      setError(null);

      const { ok, response, link: signedInLink } = await verifyPortalCode({ Code: code });

      setIsBusy(false);

      if (!ok) {
        // The server's wording, not ours. It is the only thing that distinguishes a wrong code
        // from a locked box, and it names the wait when there is one.
        setError(response?.Data?.ErrorMessage || t("react_portal_error_generic"));

        return;
      }

      // The link comes back on sign-in too, and this is the fresher of the two: a link can be
      // replaced between `open` and `verify-code`, and the one that let them in is the one
      // whose expiry the header should name.
      if (signedInLink) setLink(signedInLink);

      setIsVerified(true);
    },
    [t]
  );

  // The text of every document still to be signed, fetched as soon as the pack lands.
  //
  // The prototype puts the reader INLINE under the document rather than behind an "open" click,
  // and it is right to: the scroll gate means the contractor has to read it anyway, so a click
  // in front of it is a step that exists only to be got past.
  //
  // Only the outstanding ones. A signed document needs no reader, and fetching its body would be
  // a request per row for text nobody is going to look at.
  useEffect(() => {
    if (!pack) return undefined;

    let cancelled = false;
    const needed = pack.readDocuments.items.filter((item) => item.outstanding && !bodies[item.id]);

    needed.forEach(async (item) => {
      const { ok, document } = await fetchPortalDocument({ DocumentID: item.id });

      if (!cancelled && ok) {
        setBodies((current) => ({ ...current, [item.id]: document.body }));
      }
    });

    return () => {
      cancelled = true;
    };
    // `bodies` is deliberately out of the list: it is what this effect writes, and including it
    // would re-run the effect on its own result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pack]);

  const openDocument = useCallback(async (doc) => {
    const { ok, document } = await fetchPortalDocument({ DocumentID: doc.id });

    if (ok) setBodies((current) => ({ ...current, [doc.id]: document.body }));
  }, []);

  // One wrapper for the writes: they all answer the same way and fail the same way. A refusal
  // shows the SERVER's wording — the only thing that tells "already submitted" from "session
  // expired".
  const runWrite = useCallback(
    async (call, successKey) => {
      setIsWriting(true);
      setError(null);

      // TRY/FINALLY, ADDED 23-October-2026 — and it is not defensive padding.
      //
      // EVERY control on this screen is `disabled={isWriting}`: the upload areas, the sign
      // buttons, the personnel tick, submit. Without the `finally`, one thrown exception leaves
      // `isWriting` true for the life of the tab, and the whole pack becomes unclickable with
      // nothing on screen to say why. Found on staging, reported as "the upload area is not
      // clickable" — which is exactly what it looks like from the outside.
      //
      // `request()` turns network failure into an envelope, so the common cases do not throw.
      // What is left is the uncommon one: a payload shaped differently from what a mapper
      // expects. That is precisely the case where the screen must stay usable.
      let result;

      try {
        result = await call();
      } catch (thrown) {
        // Logged rather than swallowed: the contractor gets a sentence they can act on, and
        // whoever is debugging gets the stack.
        console.error("portal write failed", thrown);
        setError(t("react_portal_error_generic"));

        return;
      } finally {
        setIsWriting(false);
      }

      const { ok, response, pack: next } = result;

      if (!ok) {
        setError(response?.Data?.ErrorMessage || t("react_portal_error_generic"));

        return;
      }

      setPack(next);

      // WHERE TO LEAVE THE CONTRACTOR, and this changed with the ungating on 21-October.
      //
      // This used to be `setOpenStep(firstOpenStep(next))` unconditionally. That was harmless
      // while steps were gated, because the only step you could be on WAS the first open one.
      //
      // Now that any step opens in any order, it is a bug: a contractor who deliberately went to
      // step 2 to upload their insurance certificate, while step 1 was still unfinished, would be
      // thrown back to step 1 the moment the upload succeeded — and would have to navigate back
      // for the next file.
      //
      // So: move on only when the step they were on is FINISHED. Otherwise leave them where they
      // chose to be, which is the whole point of ungating.
      setOpenStep((current) => nextOpenStep(next, current));

      // Success only, and only where there is something to say. Failures stay in the banner
      // above the cards, because a failure is something to act on and a toast that disappears is
      // the wrong container for it.
      //
      // THE SERVER'S SENTENCE WINS when it sends one — Phase C, AC-P-66. The `uploaded` reply
      // says "Sent for review." on a pack that was already submitted and "Uploaded." otherwise,
      // and only the server knows which happened: the contractor pressed the same button either
      // way. Our key is the fallback, for the calls that answer with no Message.
      const serverSaid = response?.Data?.Message;

      if (serverSaid) setNotice(serverSaid);
      else if (successKey) setNotice(t(successKey));
    },
    [t]
  );

  const onSign = useCallback(
    (doc, { name, consent, readToEnd } = {}) =>
      // §5 wants three facts, and the screen already holds all of them: the tick, the typed name,
      // and whether the reader reached the end. They used to stop here — only the name was sent,
      // under a field name (`ticked`) the endpoint does not know — so every live signature would
      // have been refused. `consent` and `readToEnd` now come up from the row that observed them.
      runWrite(() =>
        signPortalDocument({
          DocumentID: doc.id,
          Name: name,
          Consent: consent,
          ReadToEnd: readToEnd,
        }),
        "react_portal_toast_signed"
      ),
    [runWrite]
  );

  const onUpload = useCallback(
    (doc, file, expiry) =>
      runWrite(
        () => uploadPortalDocument({ DocumentID: doc.id, File: file, Expiry: expiry }),
        "react_portal_toast_uploaded"
      ),
    [runWrite]
  );

  const onConfirmPersonnel = useCallback(
    () => runWrite(confirmPortalPersonnel, "react_portal_toast_personnel"),
    [runWrite]
  );

  // No toast for submit. The whole screen changes to the "pack sent" card, which says it louder
  // and does not fade — a line that disappears would be a strange way to confirm the one action
  // the contractor came here to take.
  const onSubmit = useCallback(() => runWrite(submitPortalPack), [runWrite]);

  // Complete, or in progress. NOTHING IS LOCKED ANY MORE.
  //
  // Changed 21-October-2026, from the prototype's own note: "steps are no longer gated. The
  // contractor can work on any step in any order; only the Submit button requires every step to
  // be complete."
  //
  // The gate was never what protected the pack — `POST /submit` refuses until everything is done
  // (§9), and that check is the server's. All the lock did was stop a contractor who had their
  // insurance certificate to hand from uploading it because they had not yet read the safety
  // policy. On a phone, on a site, that is a second visit to the portal for no reason.
  //
  // `firstOpenStep` is still used, to decide which card opens first. That is a convenience and
  // not a restriction: every other card can now be opened too.
  const sectionState = (step) => (isStepDone(pack, step) ? "complete" : "partial");

  const toggle = (step) => setOpenStep(openStep === step ? 0 : step);

  const readIcon = (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );

  const uploadIcon = (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );

  const peopleIcon = (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    </svg>
  );

  const submitIcon = (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  );

  // The pack's `customer` and `open`'s `company` are the same organisation named twice — the
  // pack's wins once there is one, because it is the record the rest of the page is drawn from.
  const customerName = pack?.customer || linkCompany || "";

  // "2026-11-20 23:59:59" — already local (§"do not convert `expires_at` yourself"), so the date
  // and the time are read straight off the string rather than put through a Date.
  const linkExpiryDate = link?.expiresAt ? String(link.expiresAt).slice(0, 10) : null;
  const linkExpiryTime = link?.expiresAt ? String(link.expiresAt).slice(11, 16) : null;

  // THE PACK's reference, not the visit's — two different things that have both been called
  // "reference". `mode.reference` is the pre-approval label ("RSP-PA-2026-1021-DM"), which is
  // what the prototype's footer shows and what `open` now sends; `visit.reference` is the
  // eight-character booking code from the invite email and the kiosk.
  //
  // The pack label wins where there is one, because a PRE-APPROVAL pack has no visit at all —
  // which is the case the prototype was drawn for. The booking code stays as the fallback so
  // a visit-backed pack keeps printing the code a contractor would quote on the phone.
  const footerReference =
    linkDetails?.reference || pack?.mode?.reference || pack?.visit?.reference || null;

  return (
    <div className="portal-page">
      <header className="portal-header">
        <div className="portal-header__inner">
          {/* The CUSTOMER's name leads — a contractor should see who is asking before they see
              who built the page. Ronspot's mark is in the footer. */}
          {/* The prototype puts the customer's LOGO here. We have no logo url from any endpoint,
              so the name stands in for it — which is the right fallback even once logos arrive,
              since a customer who has not uploaded one still needs naming.

              Filled before sign-in as well as after; see `linkCompany` for why that is no longer
              a disclosure. */}
          {linkDetails?.logoUrl ? (
            // The customer's own image, from their company settings — the same one their branded
            // emails carry. `alt` is the company name, so a reader who cannot see it gets exactly
            // what the fallback below would have given them.
            //
            // `onError` matters more than it looks: this is a remote image on a page open to the
            // internet, and a broken one would leave the header empty with no way back. Clearing
            // the url drops us into the text branch instead.
            <img
              className="portal-header__logo"
              src={linkDetails.logoUrl}
              alt={customerName}
              onError={() =>
                setLinkDetails((current) => ({ ...current, logoUrl: null }))
              }
            />
          ) : (
            <span className="portal-header__logo-text">{customerName}</span>
          )}

          <div className="portal-header__right">
            {/* REINSTATED 22-October-2026, against the LINK's expiry.

                We deleted this pill on 10-October on the Laravel team's instruction — "the
                portal has no closing date, so any date would be made up". Phase A gives the
                LINK one (30 days by default), and §3 is explicit that it is the link's date
                and not the visit's. Both instructions were right in turn.

                Shown BEFORE sign-in too since 24-October: `open` carries the same `link` block
                as `verify-code`, so the date is known from the first call. AC-P-74 asks for it in
                three places — "the header badge, expiry notice and footer". */}
            {/* BADGE FIRST, then the expiry — the prototype's order, and the right one: the
                badge says what this page IS, which a contractor arriving from an email needs
                before they need a deadline. We had them the other way round. */}
            <span className="portal-header__badge">{t("react_portal_badge")}</span>

            {link?.expiresAt && (
              <span className="portal-header__expiry">
                {/* The clock the pill was missing. Decorative — the sentence beside it already
                    says "Link expires", so a label here would be read out twice. */}
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                {t("react_portal_link_expires").replace(
                  "%d",
                  formatShortDate(String(link.expiresAt).slice(0, 10))
                )}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Nothing is drawn until `open` answers. A spinner rather than the code screen, because
          showing a code box to somebody whose link is dead invites them to burn their five
          attempts against a link that can never work. */}
      {isOpening ? (
        <main className="portal-content portal-content--loading">
          <p>{t("react_portal_loading")}</p>
        </main>
      ) : closed ? (
        <PortalExpired
          logoUrl={linkDetails?.logoUrl}
          closedAt={closed.closedAt}
          timezone={closed.timezone}
          contact={closed.contact}
          message={closed.message}
        />
      ) : !isVerified ? (
        <PortalVerify
          company={linkCompany}
          linkKind={link?.kind}
          maskedEmail={linkDetails?.maskedEmail}
          contact={linkDetails?.contact}
          isBusy={isBusy}
          error={error}
          codeRequested={codeRequested}
          codeRequestCount={codeRequestCount}
          onRequestCode={onRequestCode}
          onVerify={onVerify}
        />
      ) : !pack ? (
        <div className="portal-content">
          <p className="portal-section__intro">{t("react_loading")}</p>
        </div>
      ) : (
        <>
          <PortalPackHeader
            visit={pack.visit}
            customer={pack.customer}
            pack={pack}
            mode={pack.mode}
            activeStep={openStep}
          />

          <main className="portal-content">
            {/* ABOVE the step cards, and above the "pack submitted" card below it — §6.7 says it
                "sits above the step cards and always states whether the contractor is cleared".

                It is deliberately a separate thing from the submitted card: §5.1 is explicit that
                the submitted card "does not claim the contractor is cleared". A pack can be sent
                and the person still turned away at the door, because sending is something they
                do and approving is something a reviewer does. */}
            <PortalClearanceBanner siteAccess={pack.siteAccess} />

            {/* Inside the content column rather than fixed to the viewport: this page is read on
                a phone as often as not, and a floating element over a safety document the
                contractor is being gated on reading is the wrong thing to cover. */}
            <PortalToast message={notice} onDismiss={clearNotice} />

            {/* Once submitted the pack is read-only (§5, a product decision). The card says so
                first, before the steps, so nobody hunts for a control that has been withdrawn. */}
            {pack.submitted && (
              <div className="portal-done">
                <span className="portal-done__icon" aria-hidden="true">
                  <svg
                    width="30"
                    height="30"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <p className="portal-done__title">{t("react_portal_done_title")}</p>
                <p className="portal-done__sub">
                  {t("react_portal_done_sub")
                    .replace("%p", (pack.contractor.name || "").split(" ")[0])
                    .replace("%s", pack.customer || "")
                    .replace("%d", formatLongDate(pack.visit?.date))}
                </p>
                <p className="portal-done__meta">
                  {t("react_portal_done_meta")
                    .replace("%d", formatLongDate(pack.submittedAt?.slice(0, 10)))
                    .replace("%r", pack.visit?.reference || "")}
                </p>
              </div>
            )}

            {/* REMOVED 10-October-2026: the "This portal closes on …" notice.
                It was built on `access_expires`, which does not exist and is not coming —
                "the portal has no closing date. A pack stays open until the admin decides it, so
                any date would be made up." A deadline we invented is worse than no deadline. */}

            {error && <div className="portal-banner portal-banner--warn">{error}</div>}

            <PortalSection
              step={STEP.READ}
              title={t("react_portal_step_read")}
              subtitle={t("react_portal_count_docs")
                .replace("%n", String(pack.readDocuments.total))
                .replace("%o", String(pack.readDocuments.outstanding))}
              state={sectionState(STEP.READ)}
              isOpen={openStep === STEP.READ}
              onToggle={() => toggle(STEP.READ)}
              icon={readIcon}
            >
              {/* `readOnly` means SIGNATURES AND THE PERSONNEL TICK ARE LOCKED — and since Phase C
                  it means only that. Documents stay replaceable while the link is live.
                  Passing it here is what makes the narrower meaning real: before 22-October
                  nothing stopped a signature on a submitted pack, and the server would have
                  refused it with "This pack has been submitted and can no longer be changed."

                  Locked FOR GOOD, not until the next rejection: the lock lives on the link and
                  is copied to any reissued one. */}
              <PortalReadSign
                documents={pack.readDocuments.items}
                bodies={bodies}
                isBusy={isWriting}
                isLocked={pack.readOnly}
                onOpen={openDocument}
                onSign={onSign}
              />
            </PortalSection>

            <PortalSection
              step={STEP.UPLOAD}
              title={t("react_portal_step_upload")}
              subtitle={t("react_portal_count_required")
                .replace("%n", String(pack.myDocuments.total))
                .replace("%o", String(pack.myDocuments.outstanding))}
              state={sectionState(STEP.UPLOAD)}
              isOpen={openStep === STEP.UPLOAD}
              onToggle={() => toggle(STEP.UPLOAD)}
              iconTone="purple"
              icon={uploadIcon}
            >
              <PortalUploads
                customer={pack.customer}
                documents={pack.myDocuments.items}
                isBusy={isWriting}
                onUpload={onUpload}
              />
            </PortalSection>

            <PortalSection
              step={STEP.PERSONNEL}
              title={t("react_portal_step_personnel")}
              subtitle={t("react_portal_confirmation_required")}
              state={sectionState(STEP.PERSONNEL)}
              isOpen={openStep === STEP.PERSONNEL}
              onToggle={() => toggle(STEP.PERSONNEL)}
              icon={peopleIcon}
            >
              <p className="portal-section__intro">{t("react_portal_personnel_intro")}</p>

              <div className="portal-vetting">
                <label className="portal-vetting__row">
                  <input
                    type="checkbox"
                    checked={pack.personnel.confirmed}
                    disabled={pack.readOnly || isWriting}
                    onChange={onConfirmPersonnel}
                  />
                  <span className="portal-vetting__box" aria-hidden="true">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                  <span className="portal-vetting__text">
                    <p>{t("react_portal_personnel_confirm")}</p>
                    <small>
                      {t("react_portal_personnel_detail")
                        .replace("%n", String(pack.personnel?.names?.length ?? 0))
                        .replace("%c", pack.contractor.company || "")
                        .replace("%s", pack.customer || "")}
                    </small>
                  </span>
                </label>
              </div>

              <div className="portal-banner portal-banner--info">
                {t("react_portal_personnel_note").replace("%s", pack.customer || "")}
              </div>
            </PortalSection>

            {/* A RENEWAL PACK HAS NO SUBMIT STEP AT ALL (AC-P-77). Each renewal goes straight to
                the reviewer, so there is nothing to press — and a disabled Submit would be worse
                than none, because it implies a step they have failed to finish.

                `required` defaults to true when the server does not send it, so every pack
                before Phase E is unchanged.

                This conditional was MISSING until 23-October: the edit that added it failed and
                only its closing `)}` was written, which JSX renders as literal text — and it
                reached staging, where it showed as `)}` under the submit button. Lint allowed
                it (valid JSX text) and no test asserted on it. */}
            {pack.submit.required && (
              <>
            <PortalSection
              step={STEP.SUBMIT}
              title={t("react_portal_step_submit")}
              subtitle={t(
                pack.submit.canSubmit
                  ? "react_portal_ready_to_submit"
                  : "react_portal_complete_steps_first"
              )}
              state={sectionState(STEP.SUBMIT)}
              isOpen={openStep === STEP.SUBMIT}
              onToggle={() => toggle(STEP.SUBMIT)}
              icon={submitIcon}
            >
              <p className="portal-section__intro">
                {t("react_portal_review_intro").replace("%s", pack.customer || "")}
              </p>

              {/* The outstanding list comes from the SERVER, assembled alongside `can_submit`, so
                  the reasons and the button can never disagree. Each row names its step. */}
              {pack.submit.outstanding.length > 0 && (
                <ul className="portal-review">
                  {pack.submit.outstanding.map((item, index) => (
                    <li key={item.id || `row-${index}`}>
                      <span className="portal-review__step">
                        {t("react_portal_step_n").replace("%n", String(item.step))}
                      </span>
                      <span>
                        <strong>{item.title}</strong>
                        {item.why ? ` — ${item.why}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </PortalSection>

            <div className="portal-submit">
              <button
                type="button"
                className="portal-submit__btn"
                disabled={!pack.submit.canSubmit || pack.readOnly || isWriting}
                onClick={onSubmit}
              >
                {t("react_portal_submit")}
              </button>
              <p className="portal-submit__note">
                {t(
                  pack.readOnly
                    ? "react_portal_already_submitted"
                    : pack.submit.canSubmit
                      ? "react_portal_submit_ready_note"
                      : "react_portal_submit_note"
                )}
              </p>
            </div>
              </>
            )}
          </main>
        </>
      )}

      <footer className="portal-footer">
        <div className="portal-footer__inner">
          {/* FIXED 24-October-2026. This interpolated `pack?.customer`, which is null until the
              code is verified — so every reader of the verify screen was shown "...on behalf
              of ." with an empty name and a stranded full stop. The name-free wording is used
              when there is genuinely nobody to name rather than leaving the hole. */}
          <p>
            {customerName
              ? t("react_portal_footer_note").replace("%s", customerName)
              : t("react_portal_footer_note_plain")}
          </p>
          <p className="portal-footer__meta">
            <span className="portal-footer__brand">
              {t("react_portal_powered_by")} <img src={ronspotBlueLogo} alt="Ronspot" />
            </span>
            {/* The booking reference — what a contractor quotes on the phone, and the same code
                on their invite email and at the kiosk. The expiry that used to sit beside it is
                gone: there is no closing date, so there was never a date to print. */}
            {footerReference && (
              <>
                {" "}
                &middot;{" "}
                {t("react_portal_footer_meta").replace("%r", footerReference)}
              </>
            )}
            {/* AC-P-74's third place. Reinstated against the LINK's expiry — the earlier note
                that "there is no closing date" was true of the PACK and is not true of the
                link, which Phase A gives 30 days by default. */}
            {linkExpiryDate && (
              <>
                {" "}
                &middot;{" "}
                {t("react_portal_footer_expiry")
                  .replace("%d", formatShortDate(linkExpiryDate))
                  .replace("%t", linkExpiryTime || "")}
              </>
            )}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CompliancePortalUI;
